<template>
	<div class="simulated-returns-wrapper mt-50">
		<p class="body-sm font-style-italic text-color-content-secondary"
			>Hypothetical performance of investment between {{ startDateOfHypotheticalInvestment }} and today with
			dividend reinvestment enabled. <strong>Past performance does not guarantee future results.</strong></p
		>
		<div class="display-flex flex-direction-column-smo mt-100-smo mt-150-md">
			<div
				v-for="(item, index) in overviewItems"
				:key="index"
				:class="[
					'display-flex flex-direction-column-md gap-50-md align-items-center-smo justify-content-space-between-smo py-100-smo',
					{ 'bt-smo bl-md ml-125-md pl-125-md': index !== 0 }
				]"
			>
				<p class="text-color-content-secondary"
					>{{ item.label }}<sup v-if="item.footnote">{{ item.footnote }}</sup></p
				>

				<p v-if="appStore.isMobile" class="font-weight-bold mt-0 py-50">{{ item.value }}</p>
				<h3 v-else class="heading-5">{{ item.value }}</h3>
			</div>
		</div>

		<div class="module module--on-white-bg module-no-box-shadow module--no-radius mt-100-smo mt-300-md">
			<div class="module-section">
				<Tabs v-if="showTabs" :default-tab-index="defaultTabIndex" :tabs="tabs" />
				<div v-else>
					<SimulatedReturnsValueChart
						v-if="props.showValueChart && props.fund.simulatedInvestmentReturns"
						:chart-data="props.fund.simulatedInvestmentReturns"
						:initial-investment="initialInvestmentValue"
					/>
					<SimulatedReturnsNetReturnsChart
						v-if="props.showNetReturnChart && props.fund.simulatedReturnsByTimePeriod"
						:is-annualized="isAnnualized"
						:returns-by-time-period="props.fund.simulatedReturnsByTimePeriod"
						:total-investment-value="currentInvestmentValue"
						:show-distribution-footnote="isInnovationFund"
						:fund-id="props.fund.fundriseId"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { formattedPerformanceCurrencyValue } from '@utils/performance-portfolio';
import { Fund } from 'types/funds';
import { growthTechReitId } from '@constants/reit';
import SimulatedReturnsNetReturnsChart from './simulated-returns-net-returns-chart.vue';
import SimulatedReturnsValueChart from './simulated-returns-value-chart.vue';
import spacetime from 'spacetime';
import { Tab } from 'types/layout';
import Tabs from '@components/layout/tabs-component.vue';
import { useAppStore } from '@stores/app';

interface Props {
	fund: Fund;
	showValueChart: boolean;
	showNetReturnChart: boolean;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const isAnnualized = computed(
	(): boolean => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.annualized ?? false
);

const defaultTabIndex = computed((): number => {
	return tabs.value.length > 1 && props.fund.objective?.name === 'Cash flow' ? 1 : 0;
});

const currentInvestmentValue = computed(
	(): string | undefined => props.fund.simulatedInvestmentReturnsHeaders?.currentInvestmentValue
);

const initialInvestmentValue = computed(
	(): string | undefined => props.fund.simulatedInvestmentReturnsHeaders?.initialInvestmentValue
);

const netAppreciation = computed(
	(): string | undefined => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.netAppreciation
);

const netDividends = computed(
	(): string | undefined => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.netDividends
);

const startDateOfHypotheticalInvestment = computed((): string =>
	spacetime(props.fund.hypotheticalFirstInvestmentDate).format(CustomSpacetimeFormat.MONTH_SHORT_YEAR)
);

const isInnovationFund = computed((): boolean => props.fund.fundriseId === growthTechReitId);

const overviewItems = computed(
	(): Array<{
		label: string;
		value: string;
		footnote?: string;
	}> => {
		return [
			{
				label: 'Initial investment value',
				value: formattedPerformanceCurrencyValue(initialInvestmentValue.value ?? '0')
			},
			{
				label: 'Total appreciation',
				value: formattedPerformanceCurrencyValue(netAppreciation.value ?? '0')
			},
			{
				label: 'Total distributions',
				value: formattedPerformanceCurrencyValue(netDividends.value ?? '0'),
				footnote: isInnovationFund.value ? '1' : undefined
			},
			{
				label: 'Current investment value',
				value: formattedPerformanceCurrencyValue(currentInvestmentValue.value ?? '0')
			}
		];
	}
);

const tabs = computed((): Array<Tab> => {
	const tabsToReturn: Array<Tab> = [];

	if (props.showValueChart) {
		tabsToReturn.push({
			key: 'value',
			label: 'Value',
			panel: {
				component: SimulatedReturnsValueChart,
				props: {
					chartData: props.fund.simulatedInvestmentReturns,
					initialInvestment: initialInvestmentValue.value
				}
			}
		});
	}

	if (props.showNetReturnChart) {
		tabsToReturn.push({
			key: 'net-returns',
			label: 'Net return',
			panel: {
				component: SimulatedReturnsNetReturnsChart,
				props: {
					isAnnualized: isAnnualized.value,
					returnsByTimePeriod: props.fund.simulatedReturnsByTimePeriod,
					totalInvestmentValue: currentInvestmentValue.value,
					showDistributionFootnote: isInnovationFund.value
				}
			}
		});
	}

	return tabsToReturn;
});

const showTabs = computed((): boolean => tabs.value.length > 1);
</script>
<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.simulated-returns-wrapper {
	@include respond-to(md) {
		.ml-125-md {
			margin-left: 1.25rem;
		}

		.pl-125-md {
			padding-left: 1.25rem;
		}
	}
}
</style>
